import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';

// Layout
import Layout from './Layout';
import './SignUp.css';

// Icons
import MemoIconCheckmarkCircle from './icons/IconCheckmarkCircle';

// Form state management
import { useFormState, useFormDispatch, updateField, setErrors, clearErrors, setSubmitting } from './FormStateMachine';
import { validatePaymentForm } from './validation';
import { getPlanDetails } from './planDetails';

// Custom hooks
import { useCompanyData, usePromoCode, useRecurlySetup, useTouchedFields } from './hooks';

// Components
import {
  PlanSelectionSection,
  ContactInfoSection,
  PaymentDetailsSection,
  PromoCodeSection,
  ConfirmationSection,
  SponsorBanner
} from './components';

// Constants and plan service functions
import {
  INVALID_EMAIL_DOMAINS,
  DISPOSABLE_EMAIL_DOMAINS,
  TOKEN_TIMEOUT
} from './constants';

// Plan service functions for signup logic
import {
  getFullPlanCode,
  getIdentifierType,
  getIdentifierToken,
  requiresSubscriptionLength
} from './services/planService';

// Error reporting
import { Sentry } from './sentry';

// Tracking
import {
  trackFieldInteraction,
  trackFormProgression,
  trackError,
  trackConversion,
  trackPageLoad,
  trackPlanSelection,
  trackPaymentMethodSelection,
  trackFormSubmissionAttempt,
  trackFormSubmissionFailure,
  trackSignupFailure
} from './utils/gtmTracking';

/**
 * Helper function to format currency with commas
 */
const formatCurrency = (amount, showDecimals = true) => {
  if (showDecimals) {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};


/**
 * BillingPeriodSection component for lender plans
 */
const BillingPeriodSection = ({ plan, subscriptionLength, handleFieldChange, errors, formatCurrency }) => {
  if (!requiresSubscriptionLength(plan)) {
    return null;
  }

  return (
    <div className="form-section-single">
      <fieldset className="form-section-single-fieldset">
        <div className="form-section-single-legend-container">
          <legend className="form-section-single-legend">Billing period</legend>
        </div>
        <div className="plan-selection">
          <div
            className={`plan-option flex-col align-start billing-period-option ${subscriptionLength === 'monthly' ? 'selected' : ''}`}
            onClick={() => handleFieldChange('subscriptionLength', 'monthly')}
            tabIndex={0}
            role="button"
          >
            <p className="uppercase">Billed monthly</p>
            <h3>${formatCurrency(getPlanDetails(plan).monthlyCost, false)} <span>/ month</span></h3>
            {subscriptionLength === 'monthly' && (
              <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
              </span>
            )}
          </div>
          <div
            className={`plan-option flex-col align-start billing-period-option ${subscriptionLength === 'v2-annual' ? 'selected' : ''}`}
            onClick={() => handleFieldChange('subscriptionLength', 'v2-annual')}
            tabIndex={0}
            role="button"
          >
            <p className="uppercase">Billed annual</p>
            <h3>${formatCurrency(getPlanDetails(plan).annualCost, false)} <span>/ year</span></h3>
            {subscriptionLength === 'v2-annual' && (
              <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
                <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
              </span>
            )}
          </div>
        </div>
        {errors.subscriptionLength && (
          <div className="mt-2">
            <span className="error">{errors.subscriptionLength}</span>
          </div>
        )}
      </fieldset>
    </div>
  );
};

/**
 * Testimonial component
 */
const TestimonialSection = ({ showAnimation }) => (
  <div className={`well text-center mw-600px w-100 mx-auto border-box mt-8 ${showAnimation ? 'testimonial-animate' : ''}`}>
    <div className="photo-stack mx-auto"></div>
    <svg className="mt-2" width="60" height="8" viewBox="0 0 60 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_1293_6501)">
        <path d="M4 6.71429L1.57143 8L1 7.42857L1.71429 4.85714L0 3L0.285714 2.28571H2.42857L3.64286 0H4.35714L5.57143 2.28571H7.71429L8 3L6.28571 4.85714L7 7.42857L6.42857 8L4 6.71429Z" fill="white"/>
      </g>
      <g clipPath="url(#clip2_1293_6501)">
        <path d="M17 6.71429L14.5714 8L14 7.42857L14.7143 4.85714L13 3L13.2857 2.28571H15.4286L16.6429 0H17.3571L18.5714 2.28571H20.7143L21 3L19.2857 4.85714L20 7.42857L19.4286 8L17 6.71429Z" fill="white"/>
      </g>
      <g clipPath="url(#clip4_1293_6501)">
        <path d="M30 6.71429L27.5714 8L27 7.42857L27.7143 4.85714L26 3L26.2857 2.28571H28.4286L29.6429 0H30.3571L31.5714 2.28571H33.7143L34 3L32.2857 4.85714L33 7.42857L32.4286 8L30 6.71429Z" fill="white"/>
      </g>
      <g clipPath="url(#clip6_1293_6501)">
        <path d="M43 6.71429L40.5714 8L40 7.42857L40.7143 4.85714L39 3L39.2857 2.28571H41.4286L42.6429 0H43.3571L44.5714 2.28571H46.7143L47 3L45.2857 4.85714L46 7.42857L45.4286 8L43 6.71429Z" fill="white"/>
      </g>
      <g clipPath="url(#clip8_1293_6501)">
        <path d="M56 6.71429L53.5714 8L53 7.42857L53.7143 4.85714L52 3L52.2857 2.28571H54.4286L55.6429 0H56.3571L57.5714 2.28571H59.7143L60 3L58.2857 4.85714L59 7.42857L58.4286 8L56 6.71429Z" fill="white"/>
      </g>
    </svg>
    <p className="weight-600 mb-1 mt-1">Loved by 15,000+ Loan Officers and <span className="nowrap">Real Estate Agents</span></p>
  </div>
);

/**
 * Main SignUp component
 */
const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useFormDispatch();
  const formRef = useRef();

  // Form state from context
  const {
    plan,
    sponsorId,
    paymentMethod,
    cardFirstName,
    cardLastName,
    billingAddress,
    billingState,
    billingZip,
    billingCity,
    billingCountry,
    promoCode,
    accountNumber,
    accountNumberConfirmation,
    routingNumber,
    accountType,
    nameOnAccount,
    firstName,
    lastName,
    email,
    nmlsId,
    stateLicense,
    termsAccepted,
    acknowledgments,
    errors,
    isSubmitting,
    showPromoCode,
    subscriptionLength,
    referral,
    companyId,
    selectedOffice
  } = useFormState();

  // Local state
  const [freemiumAvailable, setFreemiumAvailable] = useState(false);
  const [sponsorProfile, setSponsorProfile] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [showTestimonialAnimation, setShowTestimonialAnimation] = useState(false);

  // Custom hooks
  const { touchedFields, setFieldTouched } = useTouchedFields([
    'firstName', 'lastName', 'email', 'nmlsId', 'stateLicense'
  ]);

  const {
    corporateProfile,
    officeProfiles,
    companyPlans,
    isLoadingCompanyData,
    companyDataError
  } = useCompanyData(companyId);

  const { elementsRef } = useRecurlySetup({ paymentMethod });

  // Form context for tracking
  const getFormContext = useCallback(() => ({
    plan,
    paymentMethod,
    subscriptionLength,
    sponsorId,
    promoCode
  }), [plan, paymentMethod, subscriptionLength, sponsorId, promoCode]);

  // Promo code hook
  const {
    promoCodeValidation,
    appliedPromoCodes,
    currentPromoInput,
    setCurrentPromoInput,
    isAutoApplyingPromo,
    applyPromoCode,
    removePromoCode,
    calculateTotalDiscount,
    hasFreeTrial
  } = usePromoCode({
    plan,
    subscriptionLength,
    initialPromoCode: promoCode,
    companyPlans,
    getFormContext
  });

  // Calculate costs
  const selectedPlanDetails = getPlanDetails(plan);
  const baseCost = subscriptionLength === 'monthly' || subscriptionLength === 'none'
    ? selectedPlanDetails?.monthlyCost || 0
    : selectedPlanDetails?.annualCost || 0;
  const monetaryDiscount = calculateTotalDiscount(baseCost);
  const totalDueToday = Math.max(0, baseCost + (selectedPlanDetails?.setupFee || 0) - monetaryDiscount);

  const selectNewPlanUrl = `${process.env.REACT_APP_MARKETING_URL}/pricing`;

  // Email validation
  const validateEmail = useCallback((value) => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return 'Email is required';
    }

    const emailValue = value.trim();

    if (!isEmail(emailValue, {
      allow_display_name: false,
      require_display_name: false,
      allow_utf8_local_part: false,
      require_tld: true,
      allow_ip_domain: false,
      domain_specific_validation: true,
      blacklisted_chars: '',
      host_blacklist: []
    })) {
      const error = 'Please enter a valid email address';
      setEmailError(error);
      return error;
    }

    const domain = emailValue.split('@')[1]?.toLowerCase();

    if (INVALID_EMAIL_DOMAINS.includes(domain)) {
      const error = 'Please enter a valid business email address';
      setEmailError(error);
      return error;
    }

    if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
      const error = 'Please use a permanent business email address';
      setEmailError(error);
      return error;
    }

    setEmailError('');
    return '';
  }, []);

  // Field change handler
  const handleFieldChange = useCallback((field, value) => {
    updateField(dispatch, field, value);
    trackFieldInteraction(field, 'change', value, getFormContext());

    if (field === 'email' && touchedFields.email) {
      const emailValidationError = validateEmail(value);
      if (emailValidationError) {
        trackError('validation_error', emailValidationError, 'email', getFormContext());
      }
    }

    if (field === 'plan') {
      trackPlanSelection(value, plan, getFormContext());
    }

    if (field === 'paymentMethod') {
      trackPaymentMethodSelection(value, paymentMethod, getFormContext());
    }

    if (errors[field]) {
      clearErrors(dispatch);
    }
  }, [dispatch, getFormContext, touchedFields.email, validateEmail, plan, paymentMethod, errors]);

  const handleAcknowledgmentChange = useCallback((key, checked) => {
    const updated = { ...acknowledgments, [key]: checked };
    updateField(dispatch, 'acknowledgments', updated);
    if (errors[`ack_${key}`]) {
      clearErrors(dispatch);
    }
  }, [dispatch, acknowledgments, errors]);

  const fetchSponsorProfile = useCallback(async () => {
    if (!sponsorId) return;

    try {
      const result = await fetch(`${process.env.REACT_APP_MIKASA_URL}/customer-profiles/${sponsorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (result.ok) {
        const responseData = await result.json();
        const attrs = responseData['data']['attributes'];

        if (attrs['freemium-agents'] === 'enabled') {
          setFreemiumAvailable(true);
        }

        setSponsorProfile({
          firstName: attrs['first-name'],
          lastName: attrs['last-name'],
          photoUri: attrs['photo-uri'],
          corporationName: attrs['corporation-name'],
          nmlsId: attrs['nmls'],
        });
      }
    } catch (error) {
      console.error('Failed to fetch sponsor profile:', error);
      Sentry.captureException(error);
    }
  }, [sponsorId]);

  // Form submission handler
  const handleConfirmSubscription = useCallback((e) => {
    e.preventDefault();
    clearErrors(dispatch);

    trackFormSubmissionAttempt({
      firstName, lastName, email, paymentMethod, termsAccepted, plan,
      nmlsId, stateLicense, subscriptionLength, cardFirstName, cardLastName,
      billingAddress, billingCity, billingState, billingZip, accountType,
      routingNumber, accountNumber, accountNumberConfirmation
    }, getFormContext());

    const formData = {
      plan, paymentMethod, cardFirstName, cardLastName,
      billingAddress, billingState, billingZip, billingCity, billingCountry, promoCode,
      accountNumber, accountNumberConfirmation, routingNumber, accountType, nameOnAccount,
      firstName, lastName, email, nmlsId, stateLicense, termsAccepted,
      acknowledgments, subscriptionLength,
      sponsorId, referral, companyId, selectedOffice
    };

    const validationErrors = validatePaymentForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(dispatch, validationErrors);
      Object.entries(validationErrors).forEach(([field, error]) => {
        trackError('form_validation_error', error, field, getFormContext());
      });
      trackFormSubmissionFailure('validation_errors', validationErrors, getFormContext());
      return;
    }

    setSubmitting(dispatch, true);

    const recurly = window.recurly;

    if (!elementsRef.current) {
      setErrors(dispatch, { submit: 'Payment elements not initialized' });
      setSubmitting(dispatch, false);
      return;
    }

    const tokenTimeout = setTimeout(() => {
      setErrors(dispatch, { submit: 'Tokenization timed out. Please try again.' });
      setSubmitting(dispatch, false);
    }, TOKEN_TIMEOUT);

    const sendSignupRequest = async (token) => {
      const fullPlanCode = getFullPlanCode(formData.plan, formData.subscriptionLength, companyPlans);
      const identifierToken = getIdentifierToken(formData.plan, formData);
      const identifierType = getIdentifierType(formData.plan);

      const signupData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        identifier_token: identifierToken,
        identifier_type: identifierType,
        plan_code: fullPlanCode,
        billing_token: token.id,
        sponsor_id: formData.sponsorId,
        coupon_codes: appliedPromoCodes.map(promo => promo.code).join(','),
        referral: formData.referral,
        ...(selectedOffice && { office_profile_id: selectedOffice })
      };

      try {
        const result = await fetch(`${process.env.REACT_APP_MIKASA_URL}/onboarding`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(signupData)
        });

        if (!result.ok) {
          const error = await result.json();
          if (error.errors?.base) {
            throw new Error(error.errors.base.join(', '));
          } else if (error.errors) {
            throw new Error(Array.isArray(error.errors) ? error.errors.join(', ') : JSON.stringify(error.errors));
          } else {
            throw new Error(error.status);
          }
        }

        const responseData = await result.json();

        trackConversion({
          transaction_id: responseData.transaction_id || 'unknown',
          signup_method: paymentMethod,
          user_type: identifierType
        }, getFormContext(), totalDueToday, monetaryDiscount, appliedPromoCodes, getFullPlanCode);

        navigate(`/success?authUrl=${encodeURIComponent(responseData.auth_callback_url)}`);
      } catch (error) {
        console.error('Signup error:', error);
        Sentry.captureException(error);
        setSubmitting(dispatch, false);
        setErrors(dispatch, { submit: `Failed to process signup. ${error}` });
        trackSignupFailure(error, 'api_request', getFormContext());
      }
    };

    if (paymentMethod === 'card') {
      recurly.token(elementsRef.current, formRef.current, (err, token) => {
        clearTimeout(tokenTimeout);
        if (err) {
          Sentry.captureException(err);
          setErrors(dispatch, { submit: err.message });
          setSubmitting(dispatch, false);
        } else {
          sendSignupRequest(token).finally(() => setSubmitting(dispatch, false));
        }
      });
    } else if (paymentMethod === 'bank') {
      recurly.bankAccount.token(formRef.current, (err, token) => {
        clearTimeout(tokenTimeout);
        if (err) {
          Sentry.captureException(err);
          setErrors(dispatch, { submit: err.message });
          setSubmitting(dispatch, false);
        } else {
          sendSignupRequest(token).finally(() => setSubmitting(dispatch, false));
        }
      });
    }
  }, [
    dispatch, getFormContext, elementsRef, companyPlans, appliedPromoCodes, navigate,
    firstName, lastName, email, paymentMethod, termsAccepted, acknowledgments,
    plan, nmlsId, stateLicense,
    subscriptionLength, cardFirstName, cardLastName, billingAddress, billingCity, billingState,
    billingZip, billingCountry, accountType, routingNumber, accountNumber, accountNumberConfirmation,
    nameOnAccount, promoCode, sponsorId, referral, companyId, selectedOffice, totalDueToday, monetaryDiscount
  ]);

  // Effects
  useEffect(() => {
    fetchSponsorProfile();
  }, [fetchSponsorProfile]);

  useEffect(() => {
    if (companyPlans.length > 0) {
      // Check if current plan is already a valid company plan
      const validPlanCodes = companyPlans.map(p => p.attributes?.code || p.attributes?.['plan-code']);
      const isCurrentPlanValid = validPlanCodes.includes(plan);

      // Only set to first plan if current plan is not valid
      if (!isCurrentPlanValid) {
        const firstPlanCode = validPlanCodes[0];
        if (firstPlanCode) {
          handleFieldChange('plan', firstPlanCode);
        }
      }
    }
  }, [companyPlans, plan, handleFieldChange]);

  useEffect(() => {
    if (sponsorId && (plan === 'hb-agent-solo' || plan === 'hb-agent-team')) {
      handleFieldChange('plan', 'hb-agent-partner');
    }
  }, [sponsorId, plan, handleFieldChange]);

  useEffect(() => {
    trackPageLoad(getFormContext());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (plan) {
      trackFormProgression('current_section', 'enter', {
        firstName, lastName, email, paymentMethod, termsAccepted, plan,
        nmlsId, stateLicense, subscriptionLength, cardFirstName, cardLastName,
        billingAddress, billingCity, billingState, billingZip, accountType,
        routingNumber, accountNumber, accountNumberConfirmation
      }, getFormContext());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  useEffect(() => {
    const fullName = `${cardFirstName} ${cardLastName}`.trim();
    updateField(dispatch, 'nameOnAccount', fullName);
  }, [cardFirstName, cardLastName, dispatch]);

  // Testimonial animation observer
  useEffect(() => {
    const testimonialElement = document.querySelector('.well');
    if (!testimonialElement || showTestimonialAnimation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShowTestimonialAnimation(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -25px 0px' }
    );

    observer.observe(testimonialElement);
    return () => observer.disconnect();
  }, [showTestimonialAnimation]);

  return (
    <Layout>
      <form className="mt-16" ref={formRef} onSubmit={handleConfirmSubscription} noValidate>
        <div className="single-page-container">
          {sponsorId && <SponsorBanner sponsorProfile={sponsorProfile} />}

          {/* Header */}
          <div className="form-section-single">
            <div className="form-section-single-header">
              <div className="flex space-between align-center gap-2">
                <div>
                  <h1 className="mt-0 mb-0 uppercase tracking-wide">Checkout</h1>
                  <dl className="mt-0 flex flex-row gap-1 align-baseline">
                    <dt className="lining-num header-price">${formatCurrency(totalDueToday, false)}</dt>
                    <dd className="header-label">{hasFreeTrial ? 'due today (setup only)' : 'due today'}</dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Plan Selection */}
            {!companyId && (
              <PlanSelectionSection
                plan={plan}
                sponsorId={sponsorId}
                companyId={companyId}
                freemiumAvailable={freemiumAvailable}
                isLoadingCompanyData={isLoadingCompanyData}
                companyPlans={companyPlans}
                onPlanChange={(newPlan) => handleFieldChange('plan', newPlan)}
                formatCurrency={formatCurrency}
                selectNewPlanUrl={selectNewPlanUrl}
              />
            )}

            {companyId && (
              <PlanSelectionSection
                plan={plan}
                sponsorId={sponsorId}
                companyId={companyId}
                freemiumAvailable={freemiumAvailable}
                isLoadingCompanyData={isLoadingCompanyData}
                companyPlans={companyPlans}
                onPlanChange={(newPlan) => handleFieldChange('plan', newPlan)}
                formatCurrency={formatCurrency}
                selectNewPlanUrl={selectNewPlanUrl}
              />
            )}

            {!companyId && (
              <p className="mt-4">
                <span style={{ opacity: 0.85 }}>Not a {getPlanDetails(plan)?.customerType?.toLowerCase() || 'customer'}? </span>
                <a className="pick-different-plan-link hover-underline" href={selectNewPlanUrl}>Switch your account type</a>
              </p>
            )}
          </div>

          {/* Billing Period (Lender plans only) */}
          {!isLoadingCompanyData && (
            <BillingPeriodSection
              plan={plan}
              subscriptionLength={subscriptionLength}
              handleFieldChange={handleFieldChange}
              errors={errors}
              formatCurrency={formatCurrency}
            />
          )}

          {/* Contact Info */}
          <div className="form-section-single">
            <ContactInfoSection
              firstName={firstName}
              lastName={lastName}
              email={email}
              nmlsId={nmlsId}
              stateLicense={stateLicense}
              selectedOffice={selectedOffice}
              plan={plan}
              companyId={companyId}
              touchedFields={touchedFields}
              setFieldTouched={setFieldTouched}
              emailError={emailError}
              errors={errors}
              validateEmail={validateEmail}
              handleFieldChange={handleFieldChange}
              trackFieldInteraction={trackFieldInteraction}
              getFormContext={getFormContext}
              isLoadingCompanyData={isLoadingCompanyData}
              companyDataError={companyDataError}
              officeProfiles={officeProfiles}
              corporateProfile={corporateProfile}
            />
          </div>

          {/* Payment Details */}
          <div className="form-section-single">
            <PaymentDetailsSection
              paymentMethod={paymentMethod}
              cardFirstName={cardFirstName}
              cardLastName={cardLastName}
              billingAddress={billingAddress}
              billingCity={billingCity}
              billingState={billingState}
              billingZip={billingZip}
              nameOnAccount={nameOnAccount}
              accountType={accountType}
              routingNumber={routingNumber}
              accountNumber={accountNumber}
              accountNumberConfirmation={accountNumberConfirmation}
              handleFieldChange={handleFieldChange}
              errors={errors}
            />

            {/* Promo Code */}
            <PromoCodeSection
              showPromoCode={showPromoCode}
              setShowPromoCode={(show) => updateField(dispatch, 'showPromoCode', show)}
              appliedPromoCodes={appliedPromoCodes}
              currentPromoInput={currentPromoInput}
              setCurrentPromoInput={setCurrentPromoInput}
              promoCodeValidation={promoCodeValidation}
              isAutoApplyingPromo={isAutoApplyingPromo}
              applyPromoCode={applyPromoCode}
              removePromoCode={removePromoCode}
              getFormContext={getFormContext}
            />
          </div>

          {/* Confirmation */}
          <ConfirmationSection
            selectedPlanDetails={selectedPlanDetails}
            baseCost={baseCost}
            appliedPromoCodes={appliedPromoCodes}
            totalDueToday={totalDueToday}
            hasFreeTrial={hasFreeTrial}
            termsAccepted={termsAccepted}
            acknowledgments={acknowledgments}
            onAcknowledgmentChange={handleAcknowledgmentChange}
            handleFieldChange={handleFieldChange}
            getFormContext={getFormContext}
            errors={errors}
            hasValidationErrors={Object.keys(errors).some(k => k !== 'submit' && errors[k])}
            isSubmitting={isSubmitting}
          />
        </div>
      </form>

      <TestimonialSection showAnimation={showTestimonialAnimation} />
    </Layout>
  );
};

export default SignUp;
